<template>
  <!-- Description according to the product type -->
  <div class="text-black">
    <!-- Wholesale -->
    <div v-if="productTypeId === 'wholesale' && !isEmpty(wholesaleDetails)"
         class="mt-1 text-[0.8rem] md:text-[0.844rem]">
      <span v-if="cardType === 'exclusive_offer'">
        {{ $t('user.starts_from2') }}
        <span :class="{ 'text-primary': !disableColors }">
          <strong>{{ formattedDiscountPrice }}</strong>
          <span>{{ $t('general.egy_pounds') }}</span>
        </span>
      </span>
      <span v-else :class="{ 'text-primary': !disableColors }">
        {{ $t('user.starts_from', {price: arabizeNumbers(wholesaleDetails.price).toLocaleString()}) }}
      </span>
    </div>
    <!-- Offer -->
    <div v-else-if="productTypeId === 'offer' && !isEmpty(offerDetails)"
         class="mb-1.5 text-[0.8rem] md:text-[0.844rem]">
      <!-- exclusive offers only -->
      <span v-if="cardType=== 'exclusive_offer'">
                <span>{{ $t('user.offer_description_pt1', {quantity: arabizeNumbers(offerDetails.quantity)}) }}</span>
                    <span :class="`${!disableColors ? 'text-primary' : ''}`">{{
                        $t('user.offer_description_pt2', {
                          newPrice:
                              arabizeNumbers(
                                  item.price ?
                                      Math.ceil(offerDetails.new_price * (1 - parseFloat(item.discount) / 100).toFixed(2))
                                      : offerDetails.new_price
                              ).toLocaleString()
                        })
                      }}</span>
                    <span>{{ $t('user.offer_description_pt3') }}</span>
                    <span :class="`${!disableColors ? 'text-[#FF1F00] discount-line after:bg-[#FF1F00]' : ''}`">{{
                        arabizeNumbers(offerDetails.new_price).toLocaleString()
                      }}</span> {{ " " }}
                    <span :class="`${!disableColors ? 'text-[#FF1F00]' : ''}`">{{ $t('general.egy_pounds') }}</span>
                </span>
      <!--  -->
      <span v-else>
                <span>{{ $t('user.offer_description_pt1', {quantity: arabizeNumbers(offerDetails.quantity)}) }}</span>
                <span :class="`${!disableColors ? 'text-primary' : ''}`">{{
                    $t('user.offer_description_pt2', {
                      newPrice:
                          arabizeNumbers(offerDetails.new_price).toLocaleString()
                    })
                  }}</span>
                <span>{{ $t('user.offer_description_pt3') }}</span>
                <span :class="`${!disableColors ? 'text-[#FF1F00] discount-line after:bg-[#FF1F00]' : ''}`">{{
                    arabizeNumbers(offerDetails.old_price).toLocaleString()
                  }}</span> {{ " " }}
                <span :class="`${!disableColors ? 'text-[#FF1F00]' : ''}`">{{ $t('general.egy_pounds') }}</span>
            </span>
    </div>
    <!-- Retail -->
    <div v-else-if="productTypeId === 'retail'" class="flex flex-wrap items-center gap-1">
      <template v-if="cardType === 'exclusive_offer'">
        <!-- Discount -->
        <span
            v-if="Number(item.discount) && Number(item.price) > 0"
            class="discount-line text-[0.78rem] xl:text-[0.9rem] 3xl:text-[1rem] after:bg-white bg-[#FF1F00] text-white w-max px-3 py-0.5 font-normal rounded-[0.25rem] z-[1] whitespace-nowrap"
        >
    {{ arabizeNumbers(Math.ceil(item.price)) }}
  </span>

        <!-- Price -->
        <div v-if="item.price > 0" class="flex items-center gap-0.5">
          <strong
              v-if="item && (typeof formattedPrice === 'number' || typeof formattedPrice === 'string')"
              :class="[
                'font-bold text-[clamp(9px,85%,19px)]',
                Number(item.discount) > 0 && !disableColors ? 'text-primary' : 'text-[#000000]'
              ]"
          >
            {{ formattedPrice }}
          </strong>
          <span
              :class="!disableColors ? 'text-[#515151] text-[0.855rem] 3xl:text-[0.875rem]' : ''"
          >
      {{ $t("general.egy_pounds") }}
    </span>
        </div>
      </template>
      <template v-else>
        <!-- Specified Price -->
        <div v-if="item.price > 0" class="flex items-center gap-0.5">
          <strong
              v-if="item && (typeof formattedPrice === 'number' || typeof formattedPrice === 'string')"
              :class="[
                'font-bold text-[clamp(9px,85%,19px)]',
                Number(item.discount) > 0 && !disableColors ? 'text-primary' : 'text-[#000000]'
              ]"
          >
            {{ formattedPrice }}
          </strong>
          <span
              :class="!disableColors ? Number(item.discount) > 0 ? 'text-primary' : 'text-[#515151]' : ''"
          >
      {{ $t("general.egy_pounds") }}
    </span>
        </div>

        <!-- Price will be determined when purchasing the product -->
        <small
            v-else
            class="text-[#515151] text-[0.855rem] md:text-[0.875rem]"
        >
          {{ formattedPrice }}
        </small>

        <!-- Discount Information -->
        <p
            v-if="Number(item.discount) && Number(item.price) > 0"
            class="flex flex-row md:self-end whitespace-nowrap font-[700] items-center mt-[-10px] px-2 text-center gap-[0.05rem] bg-transparent rounded-[3px]"
        >
    <span
        class="discount-line after:bg-[#9595958b] font-normal text-[0.95rem] md:text-[0.96rem] 3xl:text-[1rem] text-[#989898]"
    >
      {{
        arabizeNumbers(
            Number(item.discount) ? Number(Math.ceil(item.price)) : Number(item.price)
        ).toLocaleString()
      }}
    </span>
          <span
              :class="!disableColors ? 'text-primary text-[0.855rem] 3xl:text-[0.875rem] tracking-extrabold mx-1' : ''"
          >
      {{
              $t("general.discount_value", {
                percentage: arabizeNumbers(Math.abs(Number(item.discount))).toLocaleString(),
              })
            }}
    </span>
        </p>
      </template>

    </div>
    <div v-else> —</div>
  </div>
</template>

<script>
import {arabizeNumbers, isEmpty} from "@/helpers";

export default {
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    disableColors: {
      type: Boolean,
      required: false,
      default: false,
    },
    cardType: {
      type: String,
      required: false,
      default: "",
    },
    fromDatatable: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  methods: {
    arabizeNumbers,
    isEmpty
  },
  computed: {
    formattedDiscountPrice() {
      return arabizeNumbers(
          this.wholesaleDetails?.price * (1 - parseFloat(this.item.discount) / 100)
      ).toLocaleString();
    },
    productTypeId() {
      return this.item?.product_type;
    },
    wholesaleDetails() {
      return this.item?.wholesale_details?.[0];
    },
    offerDetails() {
      return this.item?.offer_details?.[0];
    },
    formattedPrice() {
      switch (+this.item.price) {
        case -1:
          return this.$t("general.price_will_be_specificed_later");
        case 0:
          return this.$t("general.free");
        default:
          return (Number(this.item?.discount) && this.item.discount > 0) ? 
            arabizeNumbers(Math.ceil(Number(
              this.item.price_after_discount || this.item.price* (1 - parseFloat(this.item.discount) / 100)
            )).toLocaleString()) : 
            arabizeNumbers(Number(this.item?.price).toLocaleString());
      }
    },
  }
}
</script>

<style></style>